import { knowledgeRefusal, type KnowledgeAct } from '../../services/api'
import { serverSentence } from '../../utils/refusal'

/**
 * Read a refusal off a knowledge-api response, in the merchant's terms.
 *
 * ⚠️ **A response with no `status` never reached the server.** `ApiClient.request` catches
 * `fetch` itself and returns `{ success: false, error: 'Network error' }` — or whatever the
 * browser called it — with no `status` and no `details`. That prose is written for a
 * developer, so passing it to `knowledgeRefusal` lands on the `default` arm and renders the
 * literal string "Network error" at a merchant. The token contract only applies to an
 * answer the service actually gave.
 *
 * ⚠️ It lives in its own module, rather than in the component that first needed it, because
 * PR 2's erasure control needs the SAME rule and a second copy is how the two come to
 * disagree about what a network error is. There is one implementation; both surfaces call
 * it, and `act` is the only thing they pass differently.
 *
 * ⚠️ `act` is not cosmetic: `pii_decrypt_unavailable` and `internal_error` are shared by the
 * reads and the erasure door and promise different things on each — on the door they mean
 * **nothing was destroyed**. Defaulting to `'read'` keeps the reads' copy exactly as PR 1
 * shipped it.
 *
 * ⚠️ **The two acts diverge at the TRANSPORT layer as well, and for a sharper reason.** A
 * missing `status` covers two opposite events: the request never left, or **it arrived and
 * the response was lost**. For an idempotent READ the distinction does not matter — nothing
 * is shown either way, and that statement is true in both. For a DESTRUCTIVE WRITE it
 * matters completely: the erasure may be running, and saying "nothing has been erased"
 * would be asserting an outcome the client is in no position to observe. So the door's
 * transport copy claims no outcome at all and points at the receipts, which is where the
 * answer actually exists. This is the same split as `pii_decrypt_unavailable`, one layer
 * down.
 *
 * @param res the client response
 * @param fallback what to say when the service refused without a token we know
 * @param act which act was refused — `'erasure'` for the door, `'read'` otherwise
 */
export function refusalFor(
  res: { status?: number; message?: string; error?: string; details?: unknown },
  fallback = 'Could not read what she believes.',
  act: KnowledgeAct = 'read'
): string {
  if (res.status == null) {
    return act === 'erasure'
      ? 'We could not reach Marva, so there is no way to tell from here whether this erasure started. It may have. The receipts are what know: open "Erasures in this workspace" below to see whether one was created for her, and start it again only if none was.'
      : 'We could not reach Marva just now, so nothing is shown rather than a guess. Check your connection and try again.'
  }
  const details = res.details as { reason?: unknown; accessReason?: unknown; roleReason?: unknown } | undefined
  // An unknown token falls back to the server's own sentence by the app-wide rule.
  return knowledgeRefusal(details?.reason, serverSentence(res, fallback), details, act)
}
